import React from "react";
import { formatINR } from "../../utils/format";

/**
 * Small, transparent note explaining a category (caste) fee concession.
 * Renders nothing unless there is an actual discount.
 *
 * Generalized from the inline concession markup that used to live in
 * pages/student/FeeStructure.jsx (~lines 198-209), so both the admission
 * (application) node and the tuition nodes share one presentation.
 *
 * @param {number} originalAmount     Gross fee before concession
 * @param {number} payableAmount      Net fee after concession
 * @param {number} discountPercentage Concession percentage (0 => no note)
 * @param {string} [category]         Applicant category (e.g. "obc", "sc")
 * @param {string} [label]            What the concession applies to
 */
const CasteAdjustmentNote = ({
  originalAmount,
  payableAmount,
  discountPercentage,
  category,
  label = "fee",
}) => {
  if (!discountPercentage || discountPercentage <= 0) return null;

  const categoryText = category ? ` (${String(category).toUpperCase()})` : "";

  return (
    <div className="rounded-md bg-emerald-50 border border-emerald-100 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-gray-500 line-through">
          {formatINR(originalAmount)}
        </span>
        <span className="text-gray-400">→</span>
        <span className="font-semibold text-emerald-700">
          {formatINR(payableAmount)}
        </span>
        <span className="text-xs font-medium text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
          {discountPercentage}% concession{categoryText}
        </span>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {discountPercentage}% of the {label} waived under the category concession
        {categoryText}.
      </p>
    </div>
  );
};

export default CasteAdjustmentNote;
